import { useState } from 'react';
import { Col, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { confirmation, ProjectsConstructorCard, useAppFindFormik } from '@/components';
import { TableForm } from './TableForm';
import { AddTableDto, ProjectDto, TableDto } from '@/core';
import { useDeleteTableMutation, useUpdateTableMutation } from '@/redux';
import { TableTypesEnum, useTagColor, FormValueType } from './hooks';
import classes from './TableCard.module.scss';

type CardProps = TableDto & {
  onClickMenuEdit: () => void;
  isEdit: boolean;
  stopEditCard: (id: string) => void;
  projectData: ProjectDto;
};

export const TableCard = (props: CardProps) => {
  const { name, type, columns, isEdit, id, onClickMenuEdit, stopEditCard, projectData } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const [updateTable] = useUpdateTableMutation();
  const [deleteTable] = useDeleteTableMutation();
  const tagColor = useTagColor(type);
  const initialValue: FormValueType = {
    name,
    type,
    columns: columns.map((column) => column.columnId),
  };
  const form = useAppFindFormik(name);
  const [pickedColumns, setPickedColumns] = useState<string[] | undefined>(initialValue.columns);
  const [pickedTableType, setPickedTableType] = useState(initialValue.type);
  const affectedSheetsByTable = projectData.sheets.filter((sheet) =>
    sheet.tables.some(({ tableId }) => tableId === id),
  );

  const clearForm = () => {
    setPickedTableType(TableTypesEnum.Main);
    setPickedColumns(undefined);
    form.current?.setValues({ name: '', type: TableTypesEnum.Main, columns: undefined });
  };

  const stopEdit = () => {
    setPickedTableType(initialValue.type);
    setPickedColumns(initialValue.columns);
    stopEditCard(id);
  };

  const submitUpdate = (tableData: AddTableDto) => {
    updateTable({
      tableData: {
        ...tableData,
        columns: pickedColumns?.map((columnId, index) => ({ columnId, sequence: index }))!,
      },
      id,
    })
      .unwrap()
      .then(() => stopEditCard(id));
  };
  const onClickMenuDelete = () =>
    confirmation.show({
      title: t('addTables.delete.title'),
      body: t('addTables.delete.body', { affectedSheetsQuantity: affectedSheetsByTable.length }),
      isConfirmButtonDanger: true,
      onConfirm: () => deleteTable({ id }),
      confirm: 'Delete',
    });

  return (
    <ProjectsConstructorCard
      isEdit={isEdit}
      onClickMenuDelete={onClickMenuDelete}
      onClickMenuEdit={onClickMenuEdit}
      invalidCard={!columns.length}
      editForm={
        <TableForm
          handleSubmit={submitUpdate}
          projectData={projectData}
          formUid={name}
          initialValue={initialValue}
          pickedColumns={pickedColumns}
          setPickedColumns={(columns) => setPickedColumns(columns)}
          pickedTableType={pickedTableType}
          clearForm={clearForm}
          setPickedTableType={(type) => setPickedTableType(type)}
          isEditForm
          stopEditCard={stopEdit}
        />
      }
    >
      <Col xs={6} md={3}>
        <div className={classes.tagsWrapper}>
          <Tag color={tagColor}>
            <Typography.Text ellipsis>{type}</Typography.Text>
          </Tag>
        </div>
      </Col>
      <Col span={8}>
        <Typography.Paragraph className="pe-3">{name}</Typography.Paragraph>
      </Col>
      <Col xs={10} md={13}>
        <Typography.Paragraph>{columns.length} Columns</Typography.Paragraph>
      </Col>
    </ProjectsConstructorCard>
  );
};
